import { FunctionComponent, useEffect, useState } from "react";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";
import {
  payment_request_default,
  payment_status,
  PaymentRequestModel,
  po_payments_req_status,
} from "../../model/payment_request_model";
import { urls } from "../../../../utils/urls";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import { fetchGet, fetchPut } from "../../../../service/api_client";
import LoadingWidget from "../../../../context_providers/modal/loader";
import moment from "moment";
import AttachmentViewer from "../../../../ui/new_components/common/attachment_viewer";
import { POType, PurchaseOrderModelV2 } from "../../model/purchase_order_model";
import Icon from "@mdi/react";
import { mdiChevronLeft, mdiHistory, mdiInformationOutline } from "@mdi/js";
import { currencyFormat } from "../../../../utils/currency_format";
import ModalDialog from "../../../../ui/new_components/common/modal";
import ActionHistoryAdvance from "../components/payment_request_advance_history_logs";
import {
  ModalAlignment,
  ModalType,
  useModal,
} from "../../../../context_providers/modal/modal_context";
import { ACTION, MODULE } from "../../../../utils/enums";
import { useAuth } from "../../../../context_providers/auth/authProvider";
import { POLineItemCard } from "../../purchase_order_create/components/po_line_item_card";
import { usePurchaseOrder } from "../../contexts/po-creation-context";
import { currencies } from "../../../accounts/models/party_model";
import { Project } from "../../../inventory/models/project_model";
import { StatusBadge } from "../../../../ui/new_components/common/responsive";

interface PaymentRequestAdvanceViewProps {}

const fmtDate = (d?: any) => (d ? moment(d).format("DD-MM-YYYY") : "--");

const ReadField: FunctionComponent<{
  label: string;
  value?: any;
  valueClassName?: string;
  className?: string;
}> = ({ label, value, valueClassName, className }) => (
  <div className={`flex flex-col ${className ?? ""}`}>
    <span className="text-gray-400">{label}</span>
    <span
      className={`font-semibold break-words ${
        valueClassName ?? "text-gray-700"
      }`}
    >
      {value === undefined || value === null || value === "" ? "--" : value}
    </span>
  </div>
);

const PaymentRequestAdvanceApprovalScreen: FunctionComponent<
  PaymentRequestAdvanceViewProps
> = () => {
  const isUpdatePage = useRouteMatch(
    `${urls.po_payment_request_approval}/approveAdvance/:id`,
  );
  const { isAuthorised } = useAuth();
  const { id } = useParams<{ id: string }>();
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/purchase-order/advance_payment_request`;
  const { showToast } = useToast();
  const [
    showAdvancePaymentRequestPaymentFailModal,
    setShowAdvancePaymentRequestPaymentFailModal,
  ] = useState(false);
  const [showPaymentRequestApprovalModal, setShowPaymentRequestApprovalModal] =
    useState(false);
  const [showPaymentRequestRepayModal, setShowPaymentRequestRepayModal] =
    useState(false);
  const [
    showAdvancePaymentRequestApprovalModal,
    setShowAdvancePaymentRequestApprovalModal,
  ] = useState(false);
  const [
    showAdvancePaymentRequestApprovalOnHoldModal,
    setShowAdvancePaymentRequestApprovalOnHoldModal,
  ] = useState(false);
  const [
    showAdvancePaymentRequestApprovalRejectModal,
    setShowAdvancePaymentRequestApprovalRejectModal,
  ] = useState(false);

  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [project, setProject] = useState<Project>();
  const { purchaseOrder, setPurchaseOrder, defaultProject, setDefaultProject } =
    usePurchaseOrder();
  const [data, setData] = useState<PaymentRequestModel>(
    payment_request_default || ({} as PaymentRequestModel),
  );
  const existingItems = purchaseOrder?.items || [];
  const { showModal } = useModal();

  useEffect(() => {
    if (id && isUpdatePage) {
      getData(id);
    }
  }, []);

  useEffect(() => {
    const fetchPurchaseOrder = async () => {
      if (data.po_no) {
        await fetchPurchaseOrderByPoNo(data.po_no);
      }
    };

    fetchPurchaseOrder();
  }, [data.po_no]);

  useEffect(() => {
    const fetchProjectData = async () => {
      if (purchaseOrder?.project_id && !project) {
        await getProjectData(purchaseOrder.project_id);
      }
    };

    fetchProjectData();
  }, [purchaseOrder?.project_id, project]);

  const showLog = () => {
    showModal({
      title: "Logs",
      type: ModalType.drawer,
      alignment: ModalAlignment.right,
      container: (id) => <ActionHistoryAdvance pr_id={data.id} data={data} />,
    });
  };

  const PaymentStatus = async (status: string) => {
    if (
      status === payment_status.paid &&
      (!data.utr || !data.payment_date || !data.amount_paid)
    ) {
      showToast({
        type: ToastType.error,
        text: "Need reqired fields.",
      });
      return;
    }

    if (
      status === payment_status.paid &&
      data.payment_amount < data.amount_paid + data.tds_amount
    ) {
      showToast({
        type: ToastType.error,
        text: "The paid amount and tds_amount sum should not exceed the payment amount.",
      });
      return;
    }
    if (status === payment_status.failed && !data.failed_remarks) {
      showToast({
        type: ToastType.error,
        text: "Need Payment Rejected Remarks.",
      });
      return;
    }

    if (status) {
      try {
        const res = await fetchPut(`${url}/${data.id}/payment_status`, data);

        if (!res.success) {
          showToast({ type: ToastType.error, text: res.error });
          return;
        }

        showToast({
          type: ToastType.success,
          text: "Payment Status Updated.",
        });
        history.push(urls.po_payment_request_approval);
      } catch (error) {
        showToast({
          type: ToastType.error,
          text: "Failed to update status. Try again later.",
        });
      }
    }
  };

  const PRStatus = async (status: string) => {
    if (status === po_payments_req_status.onHold && !data.onhold_reason) {
      showToast({
        type: ToastType.error,
        text: "Need PR OnHold Reason.",
      });
      return;
    }
    if (status === po_payments_req_status.rejected && !data.reject_reason) {
      showToast({
        type: ToastType.error,
        text: "Need PR Rejected Reason.",
      });
      return;
    }

    if (status) {
      try {
        const res = await fetchPut(`${url}/${data.id}/payment_approval`, data);

        if (!res.success) {
          showToast({ type: ToastType.error, text: res.error });
          return;
        }

        showToast({
          type: ToastType.success,
          text: "PR Status Updated.",
        });
        history.push(urls.po_payment_request_approval);
      } catch (error) {
        showToast({
          type: ToastType.error,
          text: "Failed to update status. Try again later.",
        });
      }
    }
  };

  const fetchPurchaseOrderByPoNo = async (
    poNo: string,
  ): Promise<PurchaseOrderModelV2 | null> => {
    setLoading(true);
    try {
      const res = await fetchGet(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/purchase-order/${poNo}`,
      );
      if (res.success) {
        if (!res.data.vendor_currency) {
          res.data.vendor_currency = currencies.rupee;
          res.data.vendor_currency_to_inr = 1;
        }

        if (res.data.items === undefined || res.data.items === null) {
          res.data.items = [];
        }

        setPurchaseOrder({ ...res.data });
        if (res.data.items[0].project) {
          setDefaultProject(res.data.items[0].project);
        }
        setLoading(false);
        return res.data as PurchaseOrderModelV2;
      } else {
        showToast({ type: ToastType.error, text: res.error });
        return null;
      }
    } catch (error) {
      showToast({ type: ToastType.error, text: (error as Error).message });
      setLoading(false);
      return null;
    }
  };

  const [isUpdating, setIsUpdating] = useState(false);

  const handleUpdate = async () => {
    if (isUpdating) return;
    setIsUpdating(true);
    try {
      await PaymentStatus(payment_status.paid);
    } finally {
      setIsUpdating(false);
    }
  };

  const getProjectData = async (id: string) => {
    setLoading(true);

    const res = await fetchGet(
      `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/project/${id}`,
    );
    if (res.success) {
      setProject({ ...res.data });
    } else showToast({ type: ToastType.error, text: res.error });
    setLoading(false);
  };

  const getData = async (id: string) => {
    try {
      setLoading(true);
      const res = await fetchGet(`${url}/${id}`);
      if (res.success) {
        const updatedData = { ...res.data, type: res.data.type }; // Update type property if necessary
        setData(updatedData); // Update state with merged data
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      showToast({ type: ToastType.error, text: "An error occurred" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {showPaymentRequestApprovalModal && (
        <ModalDialog
          id="payment_request_advance_approval_view_paid_modal"
          show={showPaymentRequestApprovalModal}
          title={"Payment Request Approve"}
          onClose={function (): void {
            setShowPaymentRequestApprovalModal(false);
            setData({
              ...data,
              payment_status: payment_status.pending,
            });
          }}
        >
          <div className="bg-white h-auto p-4 sm:p-6 rounded-md shadow-lg w-full sm:w-96">
            <h2 className="text-xl font-bold mb-4">
              Do you want to change Payment Status to Paid?
            </h2>

            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm text-gray-500">Payment Date *</label>
                <input
                  id="payment_request_advance_approval_view_paid_payment_date"
                  type="date"
                  required
                  value={
                    data?.payment_date
                      ? moment(data?.payment_date).format("YYYY-MM-DD")
                      : ""
                  }
                  className="w-full px-3 py-2 border rounded-md"
                  onChange={(e) => {
                    const selectedDate = e.target.value; // Get the selected date string (YYYY-MM-DD)
                    const date = selectedDate
                      ? new Date(new Date(selectedDate).setHours(0, 0, 0, 0)) // Convert to a date object
                      : undefined; // Reset if no date is selected

                    // Update the filter
                    setData((pv) => ({
                      ...pv,
                      payment_date: date!,
                    }));
                  }}
                />
              </div>
              {/* UTR input */}
              <div className="flex flex-row gap-2">
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-gray-500">UTR *</label>
                  <input
                    id="payment_request_advance_approval_view_paid_utr"
                    value={data?.utr || ""}
                    required
                    onChange={(e) =>
                      setData((pv) => ({
                        ...pv,
                        utr: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>

                {/* Swift input */}
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-gray-500">Swift </label>
                  <input
                    id="payment_request_advance_approval_view_paid_swift"
                    type="text"
                    value={data.swift}
                    onChange={(e) =>
                      setData((pv) => ({
                        ...pv,
                        swift: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
              </div>
              <div className="flex flex-row gap-2">
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-gray-500">TDS Amount </label>
                  <input
                    id="payment_request_advance_approval_view_paid_tds_amount"
                    type="number"
                    value={data.tds_amount}
                    onChange={(e) =>
                      setData((pv) => ({
                        ...pv,
                        tds_amount: Number(e.target.value),
                      }))
                    }
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm text-gray-500">TDS Percent </label>
                  <input
                    id="payment_request_advance_approval_view_paid_tds_percent"
                    type="number"
                    value={data.tds_percent}
                    onChange={(e) =>
                      setData((pv) => ({
                        ...pv,
                        tds_percent: Number(e.target.value),
                      }))
                    }
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm text-gray-500">Paid Amount *</label>
                <input
                  id="payment_request_advance_approval_view_paid_amount"
                  type="number"
                  value={data.amount_paid}
                  onChange={(e) =>
                    setData((pv) => ({
                      ...pv,
                      amount_paid: Number(e.target.value),
                    }))
                  }
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              {/* Remarks input */}
              <div className="flex flex-col gap-1">
                <label className="text-sm text-gray-500">Remarks </label>
                <textarea
                  id="payment_request_advance_approval_view_paid_remarks"
                  value={data.paid_remarks}
                  onChange={(e) =>
                    setData((pv) => ({
                      ...pv,
                      paid_remarks: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border rounded-md"
                  rows={4}
                ></textarea>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex justify-between mt-4">
              <button
                id="payment_request_advance_approval_view_paid_cancel_btn"
                onClick={() => {
                  setShowPaymentRequestApprovalModal(false);
                  setData({
                    ...data,
                    payment_status: payment_status.pending,
                  });
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                Cancel
              </button>
              {/* <button
                onClick={() => PaymentStatus(payment_status.paid)}
                className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
              >
                Update
              </button> */}
              <button
                id="payment_request_advance_approval_view_paid_submit_btn"
                onClick={handleUpdate}
                disabled={isUpdating}
                className={`${
                  isUpdating
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-green-500 hover:bg-blue-600"
                } text-white px-4 py-2 rounded-md`}
              >
                {isUpdating ? "Updating..." : "Update"}
              </button>
            </div>
          </div>
        </ModalDialog>
      )}
      {showPaymentRequestRepayModal && (
        <ModalDialog
          id="payment_request_advance_approval_view_repay_modal"
          show={showPaymentRequestRepayModal}
          title={"Payment Request Approve"}
          onClose={function (): void {
            setShowPaymentRequestRepayModal(false);
            setData({
              ...data,
              payment_status: payment_status.failed,
            });
          }}
        >
          <div className="bg-white h-auto p-4 sm:p-6 rounded-md shadow-lg w-full sm:w-96">
            <h2 className="text-xl font-bold mb-4">
              Do you want to change Payment Status to Paid?
            </h2>

            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm text-gray-500">Payment Date *</label>
                <input
                  id="payment_request_advance_approval_view_repay_payment_date"
                  type="date"
                  required
                  value={
                    data?.payment_date
                      ? moment(data?.payment_date).format("YYYY-MM-DD")
                      : ""
                  }
                  className="w-full px-3 py-2 border rounded-md"
                  onChange={(e) => {
                    const selectedDate = e.target.value; // Get the selected date string (YYYY-MM-DD)
                    const date = selectedDate
                      ? new Date(new Date(selectedDate).setHours(0, 0, 0, 0)) // Convert to a date object
                      : undefined; // Reset if no date is selected

                    // Update the filter
                    setData((pv) => ({
                      ...pv,
                      payment_date: date!,
                    }));
                  }}
                />
              </div>
              {/* UTR input */}
              <div className="flex flex-row gap-2">
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-gray-500">UTR *</label>
                  <input
                    id="payment_request_advance_approval_view_repay_utr"
                    value={data?.utr || ""}
                    required
                    onChange={(e) =>
                      setData((pv) => ({
                        ...pv,
                        utr: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>

                {/* Swift input */}
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-gray-500">Swift </label>
                  <input
                    id="payment_request_advance_approval_view_repay_swift"
                    type="text"
                    value={data.swift}
                    onChange={(e) =>
                      setData((pv) => ({
                        ...pv,
                        swift: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
              </div>
              <div className="flex flex-row gap-2">
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-gray-500">TDS Amount </label>
                  <input
                    id="payment_request_advance_approval_view_repay_tds_amount"
                    type="number"
                    value={data.tds_amount}
                    onChange={(e) =>
                      setData((pv) => ({
                        ...pv,
                        tds_amount: Number(e.target.value),
                      }))
                    }
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm text-gray-500">TDS Percent </label>
                  <input
                    id="payment_request_advance_approval_view_repay_tds_percent"
                    type="number"
                    value={data.tds_percent}
                    onChange={(e) =>
                      setData((pv) => ({
                        ...pv,
                        tds_percent: Number(e.target.value),
                      }))
                    }
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm text-gray-500">Paid Amount *</label>
                <input
                  id="payment_request_advance_approval_view_repay_amount"
                  type="number"
                  value={data.amount_paid}
                  onChange={(e) =>
                    setData((pv) => ({
                      ...pv,
                      amount_paid: Number(e.target.value),
                    }))
                  }
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              {/* Remarks input */}
              <div className="flex flex-col gap-1">
                <label className="text-sm text-gray-500">Remarks </label>
                <textarea
                  id="payment_request_advance_approval_view_repay_remarks"
                  value={data.paid_remarks}
                  onChange={(e) =>
                    setData((pv) => ({
                      ...pv,
                      paid_remarks: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border rounded-md"
                  rows={4}
                ></textarea>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex justify-between mt-4">
              <button
                id="payment_request_advance_approval_view_repay_cancel_btn"
                onClick={() => {
                  setShowPaymentRequestRepayModal(false);
                  setData({
                    ...data,
                    payment_status: payment_status.failed,
                  });
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                Cancel
              </button>
              {/* <button
                onClick={() => PaymentStatus(payment_status.paid)}
                className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
              >
                Update
              </button> */}
              <button
                id="payment_request_advance_approval_view_repay_submit_btn"
                onClick={handleUpdate}
                disabled={isUpdating}
                className={`${
                  isUpdating
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-green-500 hover:bg-blue-600"
                } text-white px-4 py-2 rounded-md`}
              >
                {isUpdating ? "Updating..." : "Update"}
              </button>
            </div>
          </div>
        </ModalDialog>
      )}

      {showAdvancePaymentRequestPaymentFailModal && (
        <ModalDialog
          id="payment_request_advance_approval_view_payment_fail_modal"
          show={showAdvancePaymentRequestPaymentFailModal}
          title={"Update Status"}
          onClose={() => {
            setShowAdvancePaymentRequestPaymentFailModal(false);
            setData({
              ...data,
              payment_status: payment_status.pending,
            });
          }}
        >
          <div className="bg-white p-4 sm:p-6 rounded-md shadow-lg w-full sm:w-96">
            <h2 className="text-md font-semibold mb-4">
              Do you want to change Payment Status to Payment Failed?
            </h2>
            <div className="flex flex-col gap-1">
              <label className="text-sm text-gray-500">Remarks *</label>
              <textarea
                id="payment_request_advance_approval_view_payment_fail_remarks"
                value={data.failed_remarks}
                onChange={(e) =>
                  setData({ ...data, failed_remarks: e.target.value })
                }
                className="w-full px-3 py-2 border rounded-md"
                rows={4}
              ></textarea>
            </div>
            <div className="flex justify-between mt-4">
              <button
                id="payment_request_advance_approval_view_payment_fail_cancel_btn"
                onClick={() => {
                  setShowAdvancePaymentRequestPaymentFailModal(false);
                  setData({
                    ...data,
                    payment_status: payment_status.pending,
                  });
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                No
              </button>
              <button
                id="payment_request_advance_approval_view_payment_fail_confirm_btn"
                onClick={() => PaymentStatus(payment_status.failed)}
                className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
              >
                Yes
              </button>
            </div>
          </div>
        </ModalDialog>
      )}

      {showAdvancePaymentRequestApprovalModal && (
        <ModalDialog
          id="payment_request_advance_approval_view_approve_modal"
          show={showAdvancePaymentRequestApprovalModal}
          title={"Advance Payment Request Approve"}
          onClose={() => {
            setShowAdvancePaymentRequestApprovalModal(false);
            setData({
              ...data,
              pr_status: po_payments_req_status.pending,
            });
          }}
        >
          <div className="bg-white p-4 sm:p-6 rounded-md shadow-lg w-full sm:w-96">
            <h2 className="text-md font-semibold mb-4">
              Do you want to change Payment Status to Approved?
            </h2>
            <div className="flex justify-between mt-4">
              <button
                id="payment_request_advance_approval_view_approve_cancel_btn"
                onClick={() => {
                  setShowAdvancePaymentRequestApprovalModal(false);
                  setData({
                    ...data,
                    pr_status: po_payments_req_status.pending,
                  });
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                No
              </button>
              <button
                id="payment_request_advance_approval_view_approve_confirm_btn"
                onClick={() => {
                  PRStatus(po_payments_req_status.approved);
                }}
                className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
              >
                Yes
              </button>
            </div>
          </div>
        </ModalDialog>
      )}

      {showAdvancePaymentRequestApprovalOnHoldModal && (
        <ModalDialog
          id="payment_request_advance_approval_view_onhold_modal"
          show={showAdvancePaymentRequestApprovalOnHoldModal}
          title={"Advance Payment Request OnHold"}
          onClose={() => setShowAdvancePaymentRequestApprovalOnHoldModal(false)}
        >
          <div className="bg-white p-4 sm:p-6 rounded-md shadow-lg w-full sm:w-96">
            <h2 className="text-md font-semibold mb-4">
              Do you want to change Payment Status to OnHold?
            </h2>
            <div className="flex flex-col gap-1">
              <label className="text-sm text-gray-500">Reason *</label>
              <textarea
                id="payment_request_advance_approval_view_onhold_reason"
                value={data.onhold_reason}
                onChange={(e) =>
                  setData({ ...data, onhold_reason: e.target.value })
                }
                className="w-full px-3 py-2 border rounded-md"
                rows={4}
              ></textarea>
            </div>
            <div className="flex justify-between mt-4">
              <button
                id="payment_request_advance_approval_view_onhold_cancel_btn"
                onClick={() => {
                  setShowAdvancePaymentRequestApprovalOnHoldModal(false);
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                No
              </button>
              <button
                id="payment_request_advance_approval_view_onhold_confirm_btn"
                onClick={() => PRStatus(po_payments_req_status.onHold)}
                className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
              >
                Yes
              </button>
            </div>
          </div>
        </ModalDialog>
      )}
      {showAdvancePaymentRequestApprovalRejectModal && (
        <ModalDialog
          id="payment_request_advance_approval_view_reject_modal"
          show={showAdvancePaymentRequestApprovalRejectModal}
          title={"Advance Payment Request Reject"}
          onClose={() => {
            setShowAdvancePaymentRequestApprovalRejectModal(false);
            setData({
              ...data,
              pr_status: po_payments_req_status.pending,
            });
          }}
        >
          <div className="bg-white p-4 sm:p-6 rounded-md shadow-lg w-full sm:w-96">
            <h2 className="text-md font-semibold mb-4">
              Do you want to change Payment Status to Rejected?
            </h2>
            <div className="flex flex-col gap-1">
              <label className="text-sm text-gray-500">Reason *</label>
              <textarea
                id="payment_request_advance_approval_view_reject_reason"
                value={data.reject_reason}
                onChange={(e) => {
                  setData({ ...data, reject_reason: e.target.value });
                }}
                className="w-full px-3 py-2 border rounded-md"
                rows={4}
              ></textarea>
            </div>
            <div className="flex justify-between mt-4">
              <button
                id="payment_request_advance_approval_view_reject_cancel_btn"
                onClick={() => {
                  setShowAdvancePaymentRequestApprovalRejectModal(false);
                  setData({
                    ...data,
                    pr_status: po_payments_req_status.pending,
                  });
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                No
              </button>
              <button
                id="payment_request_advance_approval_view_reject_confirm_btn"
                onClick={() => PRStatus(po_payments_req_status.rejected)}
                className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
              >
                Yes
              </button>
            </div>
          </div>
        </ModalDialog>
      )}

      <div className="sticky top-0 z-20 bg-myBgcolor px-3 sm:px-5 py-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <button
              id="payment_request_advance_approval_view_back_btn"
              className="flex items-center hover:text-gray-700 font-semibold text-sm shrink-0"
              onClick={() => {
                history.goBack();
              }}
            >
              <Icon path={mdiChevronLeft} className="h-5 w-5" /> Back
            </button>
            <div className="text-base sm:text-xl font-bold border-l-4 border-myPrimaryColor pl-2 truncate">
              Advance Payment Request Approval
              <span className="text-xs sm:text-sm font-semibold text-gray-500">
                #{data.id}
              </span>
            </div>
          </div>
          {data.id && (
            <button
              id="payment_request_advance_approval_view_history_btn"
              title="Logs"
              onClick={showLog}
              className="rounded-full p-2 hover:scale-105 transform duration-150 bg-myPrimaryColor text-white shadow cursor-pointer flex items-center justify-center shrink-0"
            >
              <Icon path={mdiHistory} className="h-5 w-5" />
            </button>
          )}
        </div>
        {(data.pr_status || data.payment_status) && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
            {data.pr_status && (
              <span className="flex items-center gap-1 text-xs text-gray-500">
                PR Status
                <StatusBadge status={data.pr_status} kind="generic" />
              </span>
            )}
            {data.payment_status && (
              <span className="flex items-center gap-1 text-xs text-gray-500">
                Payment
                <StatusBadge status={data.payment_status} kind="generic" />
              </span>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 px-3 sm:px-5">
        {/* PR Details Section */}
        <div className="flex flex-col gap-2 bg-white rounded-md p-4 sm:p-5 text-xs">
          <div className="flex flex-row justify-between items-center">
            <h3 className="text-sm text-black font-semibold">PR Details </h3>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <ReadField label="Percentage" value={data.percentage} />
            <ReadField
              label="PO No."
              value={data.po_no ? `#${data.po_no}` : undefined}
              valueClassName="text-blue-500"
            />
            <ReadField
              label="Amount"
              value={data.payment_amount}
              valueClassName="text-green-600"
            />
            <ReadField label="PI No." value={data.pi_no} />
            <ReadField label="Due date" value={fmtDate(data?.due_date)} />
            <ReadField label="PI Amount" value={data.pi_amount} />
            <ReadField label="Created On" value={fmtDate(data?.created_at)} />
            <ReadField label="PI date" value={fmtDate(data?.pi_date)} />
            <ReadField
              label="Requested By"
              value={data.created_by_name}
              className="col-span-2"
            />
          </div>
          <hr />
          <div className="flex flex-col">
            <span className="text-gray-400">Notes</span>
            <p className="font-semibold break-words text-gray-700 whitespace-pre-wrap">
              {data?.notes ? data.notes : "--"}
            </p>
          </div>
        </div>

        {/* Attachments Section */}
        <div className="flex flex-col gap-2 bg-white rounded-md p-4 sm:p-5 text-xs">
          <h3 className="text-sm text-black font-semibold">
            Attachments({data?.attachments?.length})
          </h3>
          <div className="flex flex-col gap-4">
            <div className="max-h-96 overflow-y-auto border-2 rounded-md">
              <AttachmentViewer attachments={data?.attachments} />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 px-3 sm:px-5">
        <div className="flex flex-col gap-2 my-5 bg-white rounded-md p-4 sm:p-5 text-xs">
          <div className="col-span-2 flex flex-col gap-2">
            <div className="">
              <label htmlFor="" className="text-sm text-black font-semibold ">
                Project Details
              </label>
            </div>

            <div className=" text-xs mt-2">
              <div className="grid grid-cols-4">
                <h5>Project name</h5>
                <p className="col-span-3">: {defaultProject?.name || "--"}</p>
              </div>
              <div className="grid grid-cols-4">
                <h5>Description</h5>
                <p className="col-span-3">
                  : {defaultProject?.description || "--"}
                </p>
              </div>
              <hr />
              <div className="grid grid-cols-4">
                <h5>Amount :</h5>
                <p className="col-span-3  text-right">
                  {currencyFormat(defaultProject?.totalBudget || 0)}
                </p>
              </div>
              <div className="grid grid-cols-4">
                <h5>Spent :</h5>
                <p className="col-span-3  text-right">
                  {currencyFormat(defaultProject?.spent || 0)}
                </p>
              </div>
              <hr />
              <div className="grid grid-cols-4">
                <h5>Balance : </h5>
                <p className="col-span-3 text-right">
                  {currencyFormat(
                    (defaultProject?.totalBudget || 0) -
                      (defaultProject?.spent || 0),
                  )}
                </p>
              </div>

              <div className="flex gap-1 text-gray-500 text-xs mt-2 items-center">
                <Icon path={mdiInformationOutline} size={0.8}></Icon>Note : this
                balance is real-time ,Eg: if you create a po on x date and
                balance is y then if you open the same po on z date balance may
                be v.
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-col lg:flex-row justify-between lg:justify-between gap-2 my-5 bg-white rounded-md p-4 sm:p-5 text-xs">
          <div className="flex flex-col gap-2">
            <div>
              <h3 className="text-sm text-black font-semibold">
                Vendor Details
              </h3>
            </div>
            <div className="flex flex-col gap-2">
              <ReadField label="Vendor" value={purchaseOrder?.party?.name} />
              <ReadField
                label="Type"
                value={purchaseOrder?.party?.type}
                valueClassName="text-blue-500"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <div>
              <h3 className="text-sm text-black font-semibold">
                Shipping Profile
              </h3>
            </div>
            <div className="flex flex-col gap-2">
              <ReadField
                label="Address"
                value={
                  purchaseOrder?.warehouse?.address?.city
                    ? `${purchaseOrder?.warehouse?.address?.city}, ${purchaseOrder?.warehouse?.address?.pincode}`
                    : undefined
                }
              />
            </div>
          </div>
        </div>
      </div>

      <div className="px-3 sm:px-5 my-5">
        <h3 className="text-sm font-bold mb-2">Items</h3>
        {(() => {
          const isJobWork = purchaseOrder?.type === POType["Job Work PO"];
          return (
            <div className="mt-2 flex flex-col gap-3">
              {(purchaseOrder?.items || []).map((item) => (
                <POLineItemCard
                  key={item.id}
                  variant={isJobWork ? "jobwork" : "regular"}
                  mode="view"
                  currency={purchaseOrder.vendor_currency}
                  data={item}
                  existingItems={existingItems}
                  onSubmit={() => {}}
                />
              ))}
            </div>
          );
        })()}
      </div>

      <div className="mx-3 sm:mx-5 my-5 bg-white p-4 sm:p-5 rounded-md text-xs">
        <div className="flex justify-between font-bold ">
          <p>Total</p>
          <div className="">
            {currencyFormat(
              purchaseOrder?.total ?? 0,
              purchaseOrder?.vendor_currency?.short_name || "INR",
            )}
          </div>
        </div>
        <hr />
        <div className="flex justify-between font-bold ">
          <p>Paid</p>
          <div className="">
            {currencyFormat(
              purchaseOrder?.paid ?? 0,
              purchaseOrder?.vendor_currency?.short_name || "INR",
            )}
          </div>
        </div>
        <hr />
        <div className="flex justify-between font-bold ">
          <p>Balance</p>
          <div className="">
            {currencyFormat(
              (purchaseOrder?.total ?? 0) - (purchaseOrder?.paid || 0),
              purchaseOrder?.vendor_currency.short_name || "INR",
            )}
          </div>
        </div>
      </div>
      {isAuthorised({
        module: MODULE.payment_request_approval,
        action: [ACTION.ALL],
      }) && (
        <>
          {data.payment_status !== payment_status.paid &&
            data.payment_status !== payment_status.failed && (
              <div className="sticky bottom-0 mt-3 w-full rounded-t-md bg-white shadow-sm py-3 px-3 sm:px-10 border flex justify-end">
                {/* Payment Status Buttons (Shown when pr_status is "approved") */}
                {data.pr_status === po_payments_req_status.approved && (
                  <div className="flex flex-wrap gap-2 sm:gap-4">
                    <button
                      id="payment_request_advance_approval_view_paid_btn"
                      onClick={() => {
                        setShowPaymentRequestApprovalModal(true);
                        setData({
                          ...data,
                          payment_status: payment_status.paid,
                        });
                      }}
                      className="bg-green-500 text-white px-3 py-1 text-xs rounded-md hover:bg-green-600"
                    >
                      Paid
                    </button>
                    <button
                      id="payment_request_advance_approval_view_payment_failed_btn"
                      onClick={() => {
                        setShowAdvancePaymentRequestPaymentFailModal(true);
                        setData({
                          ...data,
                          payment_status: payment_status.failed,
                        });
                      }}
                      className="bg-red-500 text-white px-3 py-1 text-xs rounded-md hover:bg-red-600"
                    >
                      Payment Failed
                    </button>
                  </div>
                )}

                {/* PR Status Buttons (Hidden when pr_status is "approved") */}
                {data.pr_status !== po_payments_req_status.approved &&
                  data.pr_status !== po_payments_req_status.withdrawn &&
                  data.pr_status !== po_payments_req_status.rejected && (
                    <div className="flex flex-wrap gap-2 sm:gap-4">
                      <button
                        id="payment_request_advance_approval_view_approve_btn"
                        onClick={() => {
                          setShowAdvancePaymentRequestApprovalModal(true);
                          setData({
                            ...data,
                            pr_status: po_payments_req_status.approved,
                          });
                        }}
                        className="bg-green-500 text-white px-3 py-1 text-xs rounded-md hover:bg-green-600"
                      >
                        Approve
                      </button>
                      <button
                        id="payment_request_advance_approval_view_onhold_btn"
                        onClick={() => {
                          setShowAdvancePaymentRequestApprovalOnHoldModal(true);
                          setData({
                            ...data,
                            pr_status: po_payments_req_status.onHold,
                          });
                        }}
                        className="bg-yellow-500 text-white px-3 py-1 text-xs rounded-md hover:bg-yellow-600"
                      >
                        On Hold
                      </button>
                      <button
                        id="payment_request_advance_approval_view_reject_btn"
                        onClick={() => {
                          setShowAdvancePaymentRequestApprovalRejectModal(true);
                          setData({
                            ...data,
                            pr_status: po_payments_req_status.rejected,
                          });
                        }}
                        className="bg-red-500 text-white px-3 py-1 text-xs rounded-md hover:bg-red-600"
                      >
                        Reject
                      </button>
                    </div>
                  )}
              </div>
            )}
          {data.payment_status === payment_status.failed && (
            <div className="sticky bottom-0 mt-3 w-full rounded-t-md bg-white shadow-sm py-3 px-3 sm:px-10 border flex justify-end">
              {/* Payment Status Buttons (Shown when pr_status is "approved") */}
              {data.pr_status === po_payments_req_status.approved && (
                <div className="flex flex-wrap gap-2 sm:gap-4">
                  <button
                    id="payment_request_advance_approval_view_repay_btn"
                    onClick={() => {
                      setShowPaymentRequestRepayModal(true);
                      setData({ ...data, payment_status: payment_status.paid });
                    }}
                    className="bg-green-500 text-white px-3 py-1 text-xs rounded-md hover:bg-green-600"
                  >
                    RePay
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {loading && <LoadingWidget />}
    </>
  );
};

export default PaymentRequestAdvanceApprovalScreen;
